import { useEffect, useMemo, useState } from "react";
import { ActionIcon, Center, Flex, Loader, Tooltip, useMantineTheme, Badge } from "@mantine/core";
import { MantineReactTable, type MRT_ColumnDef } from "mantine-react-table";
import { IconEye } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";

interface AdminInterestsTabProps {
  companyId: string;
}

const REQUEST_STATUS_LABELS: Record<string, string> = {
  REQUESTED: "Sent",
  BOTH_ACCEPTED: "Accepted",
  RECEIVER_DECLINED: "Declined",
};

const REQUEST_STATUS_COLORS: Record<string, string> = {
  REQUESTED: "blue",
  BOTH_ACCEPTED: "green",
  RECEIVER_DECLINED: "red",
};

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  PAYMENT_PENDING: "Pending",
  PAYMENT_COMPLETED: "Completed",
  PAYMENT_FAILED: "Failed",
};

const PAYMENT_STATUS_COLORS: Record<string, string> = {
  PAYMENT_PENDING: "yellow",
  PAYMENT_COMPLETED: "green",
  PAYMENT_FAILED: "red",
};

type InterestListItem = {
  id: string;
  companyName: string | null;
  companyId: string;
  requestStatus: string;
  paymentStatus: string | null;
  isArchived: boolean;
  createdAt: Date;
};

export default function AdminInterestsTab({ companyId }: AdminInterestsTabProps) {
  const navigate = useNavigate();
  const theme = useMantineTheme();

  const [interests, setInterests] = useState<InterestListItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInterests = async () => {
      setLoading(true);
      try {
        const res = await trpc.admin.getCompanyInterests.query({
          companyId,
        });

        setInterests(
          res.interests.map((i: any) => ({
            id: i.id,
            companyName: i.companyName,
            companyId: i.companyId,
            requestStatus: i.requestStatus,
            paymentStatus: i.paymentStatus,
            isArchived: i.isArchived,
            createdAt: new Date(i.createdAt),
          }))
        );
      } catch (err) {
        handleTrpcError(err);
        setInterests([]);
      } finally {
        setLoading(false);
      }
    };

    fetchInterests();
  }, [companyId]);

  const columns: MRT_ColumnDef<InterestListItem>[] = useMemo(
    () => [
      {
        accessorKey: "companyName",
        header: "Company Name",
        size: 250,
        Cell: ({ cell }) => {
          const value = cell.getValue() as string | null;
          return value || "—";
        },
      },
      {
        accessorKey: "companyId",
        header: "Company ID",
        size: 150,
        Cell: ({ cell }) => {
          const value = cell.getValue() as string;
          return value || "—";
        },
      },
      {
        accessorKey: "requestStatus",
        header: "Request Status",
        size: 150,
        filterVariant: "select",
        mantineFilterSelectProps: {
          data: Object.entries(REQUEST_STATUS_LABELS).map(([value, label]) => ({
            value,
            label,
          })),
          clearable: true,
        },
        Cell: ({ cell }) => {
          const value = cell.getValue() as string;
          const color = REQUEST_STATUS_COLORS[value] || "gray";
          const label = REQUEST_STATUS_LABELS[value] || value;
          return (
            <Badge variant="light" color={color} size="sm">
              {label}
            </Badge>
          );
        },
      },
      {
        accessorKey: "paymentStatus",
        header: "Payment Status",
        size: 150,
        filterVariant: "select",
        mantineFilterSelectProps: {
          data: Object.entries(PAYMENT_STATUS_LABELS).map(([value, label]) => ({
            value,
            label,
          })),
          clearable: true,
        },
        Cell: ({ cell }) => {
          const value = cell.getValue() as string | null;
          if (!value) {
            return <span style={{ color: theme.colors.gray[5] }}>—</span>;
          }
          const color = PAYMENT_STATUS_COLORS[value] || "gray";
          const label = PAYMENT_STATUS_LABELS[value] || value;
          return (
            <Badge variant="light" color={color} size="sm">
              {label}
            </Badge>
          );
        },
      },
    ],
    [theme.colors.gray]
  );

  if (loading) {
    return (
      <Center h="300px">
        <Loader size="lg" variant="dots" />
      </Center>
    );
  }

  return (
    <Flex direction="column" w="100%">
      <MantineReactTable
        columns={columns}
        data={interests}
        enableRowNumbers
        enableStickyHeader
        enableColumnPinning
        enableRowActions
        enableColumnResizing
        columnResizeMode="onEnd"
        layoutMode="semantic"
        enableGlobalFilter={false}
        positionActionsColumn="last"
        renderRowActions={({ row }) => (
          <Tooltip label="View Company">
            <ActionIcon
              c="#475569"
              variant="transparent"
              onClick={() => navigate(`/admin/companies/${row.original.id}`)}
            >
              <IconEye />
            </ActionIcon>
          </Tooltip>
        )}
        initialState={{
          columnPinning: { right: ["mrt-row-actions"] },
          density: "md",
        }}
        mantineTableBodyCellProps={{
          style: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: 200,
          },
        }}
        mantineTableHeadCellProps={{
          style: {
            fontWeight: 500,
            fontSize: "var(--mantine-font-size-md)",
            color: theme.colors.slate[6],
          },
        }}
        mantineTableContainerProps={{
          style: {
            maxHeight: "400px",
            minHeight: "100px",
            width: "100%",
            overflowX: "auto",
          },
        }}
        renderEmptyRowsFallback={() => (
          <tr>
            <td colSpan={columns.length + 2} style={{ textAlign: "center", padding: "2rem" }}>
              No interest requests found for this company.
            </td>
          </tr>
        )}
      />
    </Flex>
  );
}
